import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMessaging } from '../../contexts/MessagingContext';
import jobService from '../../utils/jobService';
import messagingService from '../../services/messagingService';
import { SupabaseError } from '../../utils/errorHandling';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import EnhancedInterviewScheduleModal from '../../components/interview/EnhancedInterviewScheduleModal';
import ApplicationDetailsModal from './components/ApplicationDetailsModal';
import ApplicationCard from './components/ApplicationCard';
import { LoadingState, ErrorState, EmptyState } from './components/ApplicationStates';
import { getJobDetailsUrl } from '../../utils/urlHelpers';
import {
  APPLICATION_STATUS,
  getStatusColor,
  getStatusIcon,
  getEmployerId,
  filterApplications,
  calculateApplicationStats,
  paginateApplications
} from './helpers';

const ITEMS_PER_PAGE = 12;

const INITIAL_STATE = {
  applications: [],
  loading: true,
  error: null,
  searchQuery: '',
  statusFilter: 'all',
  layout: 'list',
  currentPage: 1,
  showInterviewScheduler: false,
  selectedApplication: null,
  contactingEmployer: false,
  showApplicationDetails: false,
  selectedApplicationForDetails: null
};

const getPageNumbers = (current, total, maxVisible = 5) => {
  if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

/**
 * Main Applications Page Component
 * Displays and manages VA job applications with comprehensive filtering and search
 */
const ApplicationsPage = () => {
  const { user } = useAuth();
  const { selectConversationById } = useMessaging();
  const navigate = useNavigate();
  
  // State management
  const [state, setState] = useState(INITIAL_STATE);
  const {
    applications,
    loading,
    error,
    searchQuery,
    statusFilter,
    layout,
    currentPage,
    showInterviewScheduler,
    selectedApplication,
    contactingEmployer,
    showApplicationDetails,
    selectedApplicationForDetails
  } = state;

  // Update state helper
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Memoized filtered applications
  const filteredApplications = useMemo(() => 
    filterApplications(applications, searchQuery, statusFilter),
    [applications, searchQuery, statusFilter]
  );

  // Calculate pagination
  const paginationData = useMemo(() => 
    paginateApplications(filteredApplications, currentPage, ITEMS_PER_PAGE),
    [filteredApplications, currentPage]
  );

  // Memoized application statistics
  const applicationStats = useMemo(() => 
    calculateApplicationStats(applications),
    [applications]
  );

  // Load applications with error handling
  const loadApplications = useCallback(async () => {
    if (!user?.id) return;

    try {
      updateState({ loading: true, error: null });
      const result = await jobService.getUserApplications(user.id);
      
      if (result?.success) {
        updateState({ applications: result.data || [], loading: false });
      } else {
        updateState({ 
          error: result?.error || 'Failed to load applications',
          loading: false 
        });
      }
    } catch (err) {
      updateState({ 
        error: 'Failed to load applications. Please try again.',
        loading: false 
      });
      if (process.env.NODE_ENV === 'development') {
        console.error('Load applications error:', err);
      }
    }
  }, [user?.id, updateState]);

  useEffect(() => {
    if (user) loadApplications();
  }, [user, loadApplications]);

  // Enhanced error handling for employer contact
  const handleContactEmployer = async (application) => {
    const employerId = getEmployerId(application);
    
    if (!employerId) {
      updateState({ error: 'Unable to contact employer: Missing employer information' });
      if (process.env.NODE_ENV === 'development') {
        console.warn('Missing employer ID in application:', application);
      }
      return;
    }

    updateState({ contactingEmployer: true, error: null });
    
    try {
      const result = await messagingService.createOrGetConversation(
        user.id, 
        employerId, 
        application.job?.id
      );

      if (result.success) {
        const conversationId = result.data?.id || result.data;
        if (selectConversationById) {
          await selectConversationById(conversationId);
        }
        navigate('/messages');
      } else {
        updateState({ error: result.error || 'Failed to initiate conversation with employer' });
      }
    } catch (err) {
      updateState({ error: 'Failed to contact employer. Please try again.' });
      if (process.env.NODE_ENV === 'development') {
        console.error('Contact employer error:', err);
      }
    } finally {
      updateState({ contactingEmployer: false });
    }
  };

  // Handle interview scheduling
  const handleScheduleInterview = useCallback((application) => {
    updateState({ selectedApplication: application, showInterviewScheduler: true });
  }, [updateState]);

  const handleInterviewScheduled = useCallback(() => {
    updateState({ showInterviewScheduler: false, selectedApplication: null });
    loadApplications();
  }, [updateState, loadApplications]);

  // Handle archiving/unarchiving an application
  const handleArchiveApplication = useCallback(async (application) => {
    if (!user?.id) {
      updateState({ error: new SupabaseError('User session not found. Please log in again.').getUserFriendlyMessage() });
      return;
    }
    try {
      const isArchived = application.is_archived;
      const result = isArchived
        ? await jobService.unarchiveApplication(application.id, user?.id)
        : await jobService.archiveApplication(application.id, user?.id);

      if (result.success) {
        // Update local state after archive/unarchive API call succeeded
        setState(prev => ({
          ...prev,
          applications: prev.applications.map(app =>
            app.id === application.id
              ? { ...app, is_archived: !isArchived, archived_at: isArchived ? null : new Date().toISOString() }
              : app
          )
        }));      } else {
        updateState({ error: new SupabaseError(result.error || 'Failed to update application').getUserFriendlyMessage() });
      }
    } catch (err) {
      updateState({ error: new SupabaseError(err?.message || 'Failed to update application. Please try again.', err).getUserFriendlyMessage() });
    }
  }, [user?.id, updateState]);

  // Handle viewing application details
  const handleViewDetails = useCallback((application) => {
    updateState({ selectedApplicationForDetails: application, showApplicationDetails: true });
  }, [updateState]);

  // Event handlers
  const handleSearchChange = useCallback((e) => {
    updateState({ searchQuery: e.target.value });
  }, [updateState]);

  const handleStatusFilterChange = useCallback((e) => {
    updateState({ statusFilter: e.target.value, currentPage: 1 });
  }, [updateState]);

  const handleLayoutChange = useCallback((newLayout) => {
    updateState({ layout: newLayout });
  }, [updateState]);

  const handlePreviousPage = useCallback(() => {
    updateState({ currentPage: Math.max(1, currentPage - 1) });
  }, [currentPage, updateState]);

  const handleNextPage = useCallback(() => {
    updateState({ currentPage: Math.min(paginationData.totalPages, currentPage + 1) });
  }, [currentPage, paginationData.totalPages, updateState]);

  const handlePageChange = useCallback((page) => {
    updateState({ currentPage: page });
  }, [updateState]);

  // Clear error after timeout
  useEffect(() => {
    if (!error) return;
    let isMounted = true;
    const timer = setTimeout(() => {
      if (isMounted) updateState({ error: null });
    }, 5000);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [error, updateState]);

  // Early returns for loading and error states
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={loadApplications} />;

  return (
    <div className="min-h-screen bg-background-primary px-6">
        <div className="mb-6">
          <VAHeaderNav 
            title="Applications" 
            subtitle="View and manage your job applications" />
        </div>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">My Applications</h1>
          <p className="text-text-secondary">
            Track the status of your job applications and manage your opportunities.
          </p>
        </div>
        
        {/* Grid Layout: Main content + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Column */}
          <div className="col-span-1 lg:col-span-2">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-border-primary p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Input
                    placeholder="Search by job title or company..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    iconName="Search"
                  />
                </div>
                <div className="md:w-48">
                  <select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="interviewed">Interviewed</option>
                    <option value="hired">Hired</option>
                    <option value="rejected">Rejected</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleLayoutChange('list')}
                    className={`p-2 rounded-md border transition-colors ${
                      layout === 'list'
                        ? 'bg-primary text-white border-primary'
                        : 'border-border-primary bg-background-secondary text-text-secondary hover:bg-background-primary'
                    }`}
                    title="List view"
                  >
                    <Icon name="List" size={18} />
                  </button>
                  <button
                    onClick={() => handleLayoutChange('grid')}
                    className={`p-2 rounded-md border transition-colors ${
                      layout === 'grid'
                        ? 'bg-primary text-white border-primary'
                        : 'border-border-primary bg-background-secondary text-text-secondary hover:bg-background-primary'
                    }`}
                    title="Grid view"
                  >
                    <Icon name="Grid" size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Applications List */}
            {filteredApplications.length === 0 ? (
              <EmptyState 
                hasApplications={applications.length > 0}
                onBrowseJobs={() => navigate('/job-search')}
              />
            ) : (
              <>
                <div className={layout === 'list' ? 'space-y-4' : 'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
                  {paginationData.items.map((application) => (
                    <ApplicationCard
                      key={application.id}
                      application={application}
                      onViewJob={(app) => navigate(getJobDetailsUrl(app.job))}
                      onContactEmployer={handleContactEmployer}
                      onViewDetails={handleViewDetails}
                      onArchive={handleArchiveApplication}
                      contactingEmployer={contactingEmployer}
                      layout={layout}
                    />
                  ))}
                </div>

                {/* Pagination Controls */}
                {paginationData.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="px-3 py-2 rounded-md border border-border-primary bg-white text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-background-secondary transition-colors"
                    >
                      Previous
                    </button>
                    
                    <div className="flex gap-1">
                      {getPageNumbers(currentPage, paginationData.totalPages).map((page, idx) =>
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-3 py-2 text-text-secondary">…</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 rounded-md border transition-colors ${
                              page === currentPage
                                ? 'bg-primary text-white border-primary'
                                : 'border-border-primary bg-white text-text-primary hover:bg-background-secondary'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === paginationData.totalPages}
                      className="px-3 py-2 rounded-md border border-border-primary bg-white text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-background-secondary transition-colors"
                    >
                      Next
                    </button>

                    <div className="ml-4 text-sm text-text-secondary">
                      Page {currentPage} of {paginationData.totalPages} ({filteredApplications.length} total)
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Sidebar - Application Summary */}
          <div className="col-span-1">
            {applications.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-border-primary p-6 sticky top-20">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Application Summary</h3>
                <div className="grid grid-cols-1 gap-4">
                  {applicationStats.map(({ status, count }) => (
                    <div key={status} className="flex items-center gap-3 border-b border-border-secondary pb-3 last:border-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(status)}`}>
                        <Icon name={getStatusIcon(status)} size={18} />
                      </div>
                      <div>
                        <div className="text-xl font-bold text-text-primary">{count}</div>
                        <div className="text-sm text-text-secondary capitalize">{status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Application Details Modal */}
      {showApplicationDetails && selectedApplicationForDetails && (
        <ApplicationDetailsModal
          application={selectedApplicationForDetails}
          onClose={() => updateState({ showApplicationDetails: false, selectedApplicationForDetails: null })}
        />
      )}

      {/* Interview Scheduler Modal */}
      {showInterviewScheduler && selectedApplication && (
        <EnhancedInterviewScheduleModal
          isOpen={true}
          onClose={() => updateState({ showInterviewScheduler: false, selectedApplication: null })}
          onSubmit={handleInterviewScheduled}
          selectedCandidate={{
            id: user?.id,
            name: user?.user_metadata?.full_name || user?.email,
            job_title: selectedApplication.job?.title || 'Position'
          }}
          initialData={{
            jobId: selectedApplication.job?.id,
            employerId: getEmployerId(selectedApplication),
            employerName: selectedApplication.job?.employer?.company_name || selectedApplication.job?.company_name || 'Employer',
            job_title: selectedApplication.job?.title || 'Position',
            applicationId: selectedApplication.id,
            companyName: selectedApplication.job?.employer?.company_name || selectedApplication.job?.company_name || ''
          }}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
};

export default ApplicationsPage;


